import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Package, Settings, GripVertical, Eye, EyeOff, ArrowUp, ArrowDown, ArrowUpDown, FolderOpen, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { API } from '@/lib/apiBase';
import { cn } from '@/lib/utils';
import { filterActiveCategories, isCategoryActive } from '@/lib/categoryUtils';
import { isProductActive } from '@/lib/productUtils';

const VAT_RATES = [
  { value: '0', label: '0%' },
  { value: '5.5', label: '5,5%' },
  { value: '10', label: '10%' },
  { value: '20', label: '20%' },
];

// All available columns for products with their labels and sortable flag
const PRODUCT_COLUMNS = [
  { key: 'name', label: 'Nom', required: true, sortable: true },
  { key: 'description', label: 'Description', required: false, sortable: true },
  { key: 'category_name', label: 'Catégorie', required: false, sortable: true },
  { key: 'default_vat_rate', label: 'TVA par défaut', required: false, sortable: true },
];

// All available columns for categories
const CATEGORY_COLUMNS = [
  { key: 'name', label: 'Nom', required: true, sortable: true },
];

const DEFAULT_PRODUCT_VISIBLE = ['name', 'description', 'category_name', 'default_vat_rate'];
const DEFAULT_PRODUCT_ORDER = ['name', 'description', 'category_name', 'default_vat_rate'];
const DEFAULT_CATEGORY_VISIBLE = ['name'];
const DEFAULT_CATEGORY_ORDER = ['name'];

export const Products = () => {
  const { token, canEdit } = useAuth();
  
  // View mode: 'products' or 'categories'
  const [viewMode, setViewMode] = useState('products');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productFormData, setProductFormData] = useState({
    name: '',
    description: '',
    default_vat_rate: '20',
    category_id: '',
    is_active: true,
  });
  const [productAccordionOpen, setProductAccordionOpen] = useState('description');

  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    is_active: true,
  });
  const [categoryAccordionOpen, setCategoryAccordionOpen] = useState(undefined);

  // Settings state
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Product column preferences
  const [productVisibleColumns, setProductVisibleColumns] = useState(DEFAULT_PRODUCT_VISIBLE);
  const [productColumnOrder, setProductColumnOrder] = useState(DEFAULT_PRODUCT_ORDER);
  
  // Category column preferences  
  const [categoryVisibleColumns, setCategoryVisibleColumns] = useState(DEFAULT_CATEGORY_VISIBLE);
  const [categoryColumnOrder, setCategoryColumnOrder] = useState(DEFAULT_CATEGORY_ORDER);
  
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Load user preferences
  const loadPreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/preferences/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const prefs = response.data;
      
      if (prefs.visible_columns && prefs.visible_columns.length > 0) {
        // Merge saved preferences with new columns that may have been added
        const allColumnKeys = PRODUCT_COLUMNS.map(c => c.key);
        const savedVisible = prefs.visible_columns.filter(k => allColumnKeys.includes(k));
        // Add any new columns that weren't in saved preferences (as visible by default)
        const newColumns = allColumnKeys.filter(k => !prefs.visible_columns.includes(k));
        setProductVisibleColumns([...savedVisible, ...newColumns]);
      }
      if (prefs.column_order && prefs.column_order.length > 0) {
        // Merge saved order with new columns
        const allColumnKeys = PRODUCT_COLUMNS.map(c => c.key);
        const savedOrder = prefs.column_order.filter(k => allColumnKeys.includes(k));
        // Add any new columns at the end
        const newColumns = allColumnKeys.filter(k => !prefs.column_order.includes(k));
        setProductColumnOrder([...savedOrder, ...newColumns]);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setPrefsLoaded(true);
    }
  }, [token]);

  // Save user preferences
  const savePreferences = useCallback(async (visible, order) => {
    try {
      await axios.put(`${API}/user/preferences/products`, {
        page: 'products',
        visible_columns: visible,
        column_order: order
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Préférences sauvegardées');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Erreur lors de la sauvegarde des préférences');
    }
  }, [token]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        params: { include_inactive: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        params: { include_inactive: true },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Erreur lors du chargement des catégories');
    }
  }, [token]);

  useEffect(() => {
    loadPreferences();
    fetchProducts();
    fetchCategories();
  }, [loadPreferences, fetchProducts, fetchCategories]);

  // Product handlers
  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setProductFormData({
        name: product.name || '',
        description: product.description || '',
        default_vat_rate: String(product.default_vat_rate !== undefined && product.default_vat_rate !== null ? product.default_vat_rate : 20),
        category_id: product.category_id || '',
        is_active: product.is_active !== false,
      });
    } else {
      setSelectedProduct(null);
      setProductFormData({
        name: '',
        description: '',
        default_vat_rate: '20',
        category_id: '',
        is_active: true,
      });
    }
    setProductAccordionOpen(product ? undefined : 'description');
    setProductDialogOpen(true);
  };

  const handleSubmitProduct = async () => {
    if (!productFormData.name.trim()) {
      toast.error('Le nom du produit est requis');
      return;
    }
    if (!productFormData.category_id) {
      toast.error('La catégorie est requise');
      return;
    }

    try {
      const payload = {
        name: productFormData.name,
        description: productFormData.description,
        default_vat_rate: parseFloat(productFormData.default_vat_rate),
        category_id: productFormData.category_id,
        is_active: productFormData.is_active !== false,
      };

      if (selectedProduct) {
        await axios.put(`${API}/products/${selectedProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit créé');
      }

      setProductDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      await axios.delete(`${API}/products/${selectedProduct.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit supprimé');
      setDeleteProductDialogOpen(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Category handlers
  const handleOpenCategoryDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryFormData({
        name: category.name || '',
        is_active: category.is_active !== false,
      });
    } else {
      setSelectedCategory(null);
      setCategoryFormData({
        name: '',
        is_active: true,
      });
    }
    setCategoryAccordionOpen(category ? undefined : 'nom');
    setCategoryDialogOpen(true);
  };

  const handleSubmitCategory = async () => {
    if (!categoryFormData.name.trim()) {
      toast.error('Le nom de la catégorie est requis');
      return;
    }

    try {
      if (selectedCategory) {
        await axios.put(`${API}/categories/${selectedCategory.id}`, categoryFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie mise à jour');
      } else {
        await axios.post(`${API}/categories`, categoryFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Catégorie créée');
      }

      setCategoryDialogOpen(false);
      fetchCategories();
      fetchProducts(); // Refresh products in case category name changed
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      await axios.delete(`${API}/categories/${selectedCategory.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Catégorie supprimée');
      setDeleteCategoryDialogOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Column visibility toggle
  const toggleColumn = (columnKey) => {
    const columns = viewMode === 'products' ? PRODUCT_COLUMNS : CATEGORY_COLUMNS;
    const column = columns.find(c => c.key === columnKey);
    if (column?.required) return;
    
    if (viewMode === 'products') {
      setProductVisibleColumns(prev => {
        if (prev.includes(columnKey)) {
          return prev.filter(k => k !== columnKey);
        } else {
          return [...prev, columnKey];
        }
      });
    } else {
      setCategoryVisibleColumns(prev => {
        if (prev.includes(columnKey)) {
          return prev.filter(k => k !== columnKey);
        } else {
          return [...prev, columnKey];
        }
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    if (draggedColumn === columnKey) return;
    
    const setOrder = viewMode === 'products' ? setProductColumnOrder : setCategoryColumnOrder;
    setOrder(prev => {
      const newOrder = [...prev];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(columnKey);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
      }
      return newOrder;
    });
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
  };

  // Save settings
  const handleSaveSettings = () => {
    if (viewMode === 'products') {
      savePreferences(productVisibleColumns, productColumnOrder);
    }
    setSettingsDialogOpen(false);
  };

  // Reset settings
  const handleResetSettings = () => {
    if (viewMode === 'products') {
      setProductVisibleColumns(DEFAULT_PRODUCT_VISIBLE);
      setProductColumnOrder(DEFAULT_PRODUCT_ORDER);
    } else {
      setCategoryVisibleColumns(DEFAULT_CATEGORY_VISIBLE);
      setCategoryColumnOrder(DEFAULT_CATEGORY_ORDER);
    }
  };

  // Handle column sort
  const handleSort = (columnKey) => {
    const columns = viewMode === 'products' ? PRODUCT_COLUMNS : CATEGORY_COLUMNS;
    const column = columns.find(c => c.key === columnKey);
    if (!column?.sortable) return;
    
    if (sortColumn === columnKey) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    const columns = viewMode === 'products' ? PRODUCT_COLUMNS : CATEGORY_COLUMNS;
    const column = columns.find(c => c.key === columnKey);
    if (!column?.sortable) return null;
    
    if (sortColumn !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-muted-foreground/50" />;
    }
    
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1 text-primary" />
      : <ArrowDown className="w-4 h-4 ml-1 text-primary" />;
  };

  // Get display columns
  const displayColumns = useMemo(() => {
    const columns = viewMode === 'products' ? PRODUCT_COLUMNS : CATEGORY_COLUMNS;
    const order = viewMode === 'products' ? productColumnOrder : categoryColumnOrder;
    const visible = viewMode === 'products' ? productVisibleColumns : categoryVisibleColumns;
    
    return order
      .filter(key => visible.includes(key))
      .map(key => columns.find(c => c.key === key))
      .filter(Boolean);
  }, [viewMode, productColumnOrder, categoryColumnOrder, productVisibleColumns, categoryVisibleColumns]);

  // Get cell value for products
  const getProductCellValue = (product, columnKey) => {
    switch (columnKey) {
      case 'name':
        return (
          <span className="inline-flex items-center gap-2 font-medium">
            {product.name}
            {!isProductActive(product) && (
              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-500">
                Désactivé
              </span>
            )}
          </span>
        );
      case 'description':
        return <span className="text-muted-foreground">{product.description || '-'}</span>;
      case 'category_name':
        return product.category_name || '-';
      case 'default_vat_rate':
        return `${product.default_vat_rate}%`;
      default:
        return product[columnKey] || '-';
    }
  };

  // Get cell value for categories
  const getCategoryCellValue = (category, columnKey) => {
    switch (columnKey) {
      case 'name':
        return (
          <span className="inline-flex items-center gap-2 font-medium">
            {category.name}
            {!isCategoryActive(category) && (
              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-500">
                Désactivée
              </span>
            )}
          </span>
        );
      default:
        return category[columnKey] || '-';
    }
  };

  const productCategoryOptions = useMemo(() => {
    const active = filterActiveCategories(categories);
    if (
      productFormData.category_id &&
      !active.some((cat) => cat.id === productFormData.category_id)
    ) {
      const current = categories.find((cat) => cat.id === productFormData.category_id);
      if (current) return [...active, current];
    }
    return active;
  }, [categories, productFormData.category_id]);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let result = products.filter(product =>
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (sortColumn && viewMode === 'products') {
      result = [...result].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        if (sortColumn === 'default_vat_rate') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [products, searchTerm, sortColumn, sortDirection, viewMode]);

  // Filter and sort categories
  const filteredCategories = useMemo(() => {
    let result = categories.filter(category =>
      category.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (sortColumn && viewMode === 'categories') {
      result = [...result].sort((a, b) => {
        let aVal = a[sortColumn];
        let bVal = b[sortColumn];
        
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [categories, searchTerm, sortColumn, sortDirection, viewMode]);

  // Pagination
  const currentData = viewMode === 'products' ? filteredProducts : filteredCategories;
  const totalPages = Math.ceil(currentData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return currentData.slice(start, start + itemsPerPage);
  }, [currentData, currentPage, itemsPerPage]);

  // Reset page when view mode, search or items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [viewMode, searchTerm, itemsPerPage]);

  // Reset sort when changing view mode
  useEffect(() => {
    setSortColumn(null);
    setSortDirection('asc');
  }, [viewMode]);

  if (loading || !prefsLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentColumns = viewMode === 'products' ? PRODUCT_COLUMNS : CATEGORY_COLUMNS;
  const currentOrder = viewMode === 'products' ? productColumnOrder : categoryColumnOrder;
  const currentVisible = viewMode === 'products' ? productVisibleColumns : categoryVisibleColumns;

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Produits & Catégories</h1>
        <p className="text-muted-foreground">Gérez les produits, services et catégories</p>
      </div>

      {/* Toolbar: Tabs + Search + Buttons */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
            <TabsList>
              <TabsTrigger value="products" data-testid="tab-products">
                <Package className="w-4 h-4 mr-2" />
                Produits ({products.length})
              </TabsTrigger>
              <TabsTrigger value="categories" data-testid="tab-categories">
                <FolderOpen className="w-4 h-4 mr-2" />
                Catégories ({categories.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={viewMode === 'products' ? 'Rechercher un produit...' : 'Rechercher une catégorie...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={() => setSettingsDialogOpen(true)}
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          {canEdit && viewMode === 'products' && (
            <Button onClick={() => handleOpenProductDialog()} data-testid="add-product-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau produit
            </Button>
          )}
          {canEdit && viewMode === 'categories' && (
            <Button onClick={() => handleOpenCategoryDialog()} data-testid="add-category-btn">
              <FolderOpen className="w-4 h-4 mr-2" />
              Nouvelle catégorie
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              {displayColumns.map((column) => (
                <TableHead 
                  key={column.key}
                  className={column.sortable ? 'cursor-pointer select-none hover:bg-muted/50 transition-colors' : ''}
                  onClick={() => column.sortable && handleSort(column.key)}
                  data-testid={`column-header-${column.key}`}
                >
                  <div className="flex items-center">
                    {column.label}
                    {getSortIcon(column.key)}
                  </div>
                </TableHead>
              ))}
              {canEdit && <TableHead className="w-24">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {viewMode === 'products' ? (
              paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayColumns.length + (canEdit ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun produit trouvé</p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((product) => (
                  <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                    {displayColumns.map((column) => (
                      <TableCell key={column.key}>
                        {getProductCellValue(product, column.key)}
                      </TableCell>
                    ))}
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenProductDialog(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedProduct(product);
                              setDeleteProductDialogOpen(true);
                            }}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )
            ) : (
              paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayColumns.length + (canEdit ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                    <FolderOpen className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucune catégorie trouvée</p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((category) => (
                  <TableRow key={category.id} data-testid={`category-row-${category.id}`}>
                    {displayColumns.map((column) => (
                      <TableCell key={column.key}>
                        {getCategoryCellValue(category, column.key)}
                      </TableCell>
                    ))}
                    {canEdit && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenCategoryDialog(category)}
                            data-testid={`edit-category-${category.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedCategory(category);
                              setDeleteCategoryDialogOpen(true);
                            }}
                            data-testid={`delete-category-${category.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {currentData.length > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Afficher</span>
            <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>par page</span>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} sur {totalPages || 1}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0}>›</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>»</Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="bg-card max-w-md" data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Paramètres du tableau</DialogTitle>
            <DialogDescription>
              Sélectionnez les colonnes à afficher et réorganisez-les par glisser-déposer
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-sm font-medium mb-3 block">Colonnes visibles ({viewMode === 'products' ? 'Produits' : 'Catégories'})</Label>
            <div className="space-y-1 border rounded-lg p-2 bg-muted/30">
              {currentOrder.map((columnKey) => {
                const column = currentColumns.find(c => c.key === columnKey);
                if (!column) return null;
                const isVisible = currentVisible.includes(columnKey);
                const isDragging = draggedColumn === columnKey;
                
                return (
                  <div
                    key={columnKey}
                    draggable
                    onDragStart={(e) => handleDragStart(e, columnKey)}
                    onDragOver={(e) => handleDragOver(e, columnKey)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center gap-3 p-2 rounded-md cursor-move
                      ${isDragging ? 'bg-primary/20 opacity-50' : 'hover:bg-muted'}
                      ${!isVisible ? 'opacity-50' : ''}
                      transition-all duration-150
                    `}
                    data-testid={`column-item-${columnKey}`}
                  >
                    <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <Checkbox
                      id={`col-${columnKey}`}
                      checked={isVisible}
                      onCheckedChange={() => toggleColumn(columnKey)}
                      disabled={column.required}
                      data-testid={`column-checkbox-${columnKey}`}
                    />
                    <label 
                      htmlFor={`col-${columnKey}`}
                      className="flex-1 text-sm cursor-pointer select-none"
                    >
                      {column.label}
                      {column.required && (
                        <span className="text-xs text-muted-foreground ml-2">(obligatoire)</span>
                      )}
                    </label>
                    {isVisible ? (
                      <Eye className="w-4 h-4 text-green-500" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Glissez les éléments pour réorganiser les colonnes
            </p>
          </div>

          <DialogFooter className="flex justify-between sm:justify-between">
            <Button variant="ghost" onClick={handleResetSettings} data-testid="reset-settings-btn">
              Réinitialiser
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product sidebar */}
      <Sheet open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <SheetContent
          side="right"
          className="flex h-full w-[min(25vw,540px)] min-w-[300px] max-w-full flex-col gap-0 border-l border-border bg-card p-0 sm:max-w-none"
          data-testid="product-dialog"
        >
          <SheetHeader className="shrink-0 border-b border-border/60 px-6 py-5 pr-14 text-left">
            <SheetTitle>
              {selectedProduct ? 'Modifier le produit' : 'Nouveau produit'}
            </SheetTitle>
            <SheetDescription>
              {selectedProduct
                ? 'Modifiez les informations du produit'
                : 'Ajoutez un nouveau produit ou service'}
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <Accordion
              type="single"
              collapsible
              value={productAccordionOpen}
              onValueChange={setProductAccordionOpen}
              className="w-full border-0"
            >
              <AccordionItem value="description" className="border-0 border-b border-border/60">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Description
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Description du produit
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="product-name">Nom *</Label>
                      <Input
                        id="product-name"
                        value={productFormData.name}
                        onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                        placeholder="Ex: Abonnement téléphonique"
                        data-testid="product-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product-description">Description</Label>
                      <Input
                        id="product-description"
                        value={productFormData.description}
                        onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                        placeholder="Description du produit..."
                        data-testid="product-description-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product-category">Catégorie *</Label>
                      <Select
                        value={productFormData.category_id}
                        onValueChange={(value) => setProductFormData({ ...productFormData, category_id: value })}
                      >
                        <SelectTrigger data-testid="product-category-select">
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          {productCategoryOptions.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                              {!isCategoryActive(cat) ? ' (désactivée)' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {productCategoryOptions.length === 0 && (
                        <p className="text-xs text-amber-600">Aucune catégorie disponible. Créez d&apos;abord une catégorie.</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product-vat">TVA par défaut *</Label>
                      <Select
                        value={productFormData.default_vat_rate}
                        onValueChange={(value) => setProductFormData({ ...productFormData, default_vat_rate: value })}
                      >
                        <SelectTrigger data-testid="product-vat-select">
                          <SelectValue placeholder="Sélectionner un taux" />
                        </SelectTrigger>
                        <SelectContent>
                          {VAT_RATES.map((rate) => (
                            <SelectItem key={rate.value} value={rate.value}>
                              {rate.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="statut" className="border-0 border-b border-border/60 last:border-b-0">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Statut
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Statut du produit
                    </p>
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Statut actuel</p>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                        <button
                          type="button"
                          onClick={() => setProductFormData({ ...productFormData, is_active: true })}
                          className="inline-flex items-center gap-3 text-left text-sm font-medium text-foreground transition-opacity hover:opacity-90"
                          data-testid="product-status-active"
                        >
                          <span
                            className={cn(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[3px] border-2 border-orange-500 transition-colors',
                              productFormData.is_active !== false
                                ? 'bg-orange-500'
                                : 'bg-transparent',
                            )}
                            aria-hidden
                          >
                            {productFormData.is_active !== false && (
                              <Check className="h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </span>
                          Activé
                        </button>
                        <button
                          type="button"
                          onClick={() => setProductFormData({ ...productFormData, is_active: false })}
                          className="inline-flex items-center gap-3 text-left text-sm font-medium text-foreground transition-opacity hover:opacity-90"
                          data-testid="product-status-inactive"
                        >
                          <span
                            className={cn(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[3px] border-2 border-orange-500 transition-colors',
                              productFormData.is_active === false
                                ? 'bg-orange-500'
                                : 'bg-transparent',
                            )}
                            aria-hidden
                          >
                            {productFormData.is_active === false && (
                              <Check className="h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </span>
                          Désactivé
                        </button>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <SheetFooter className="mt-auto shrink-0 border-t border-border/60 px-6 py-4 sm:justify-end sm:space-x-2">
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmitProduct} data-testid="product-submit-btn">
              {selectedProduct ? 'Enregistrer' : 'Créer'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Category sidebar */}
      <Sheet open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <SheetContent
          side="right"
          className="flex h-full w-[min(25vw,540px)] min-w-[300px] max-w-full flex-col gap-0 border-l border-border bg-card p-0 sm:max-w-none"
          data-testid="category-dialog"
        >
          <SheetHeader className="shrink-0 border-b border-border/60 px-6 py-5 pr-14 text-left">
            <SheetTitle>
              {selectedCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </SheetTitle>
            <SheetDescription>
              {selectedCategory
                ? 'Modifiez le nom de la catégorie'
                : 'Ajoutez une nouvelle catégorie de produits'}
            </SheetDescription>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <Accordion
              type="single"
              collapsible
              value={categoryAccordionOpen}
              onValueChange={setCategoryAccordionOpen}
              className="w-full border-0"
            >
              <AccordionItem value="nom" className="border-0 border-b border-border/60">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Nom
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-3 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Nom de la catégorie
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="category-name">Nom *</Label>
                      <Input
                        id="category-name"
                        value={categoryFormData.name}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                        placeholder="Ex: Téléphonie, Internet..."
                        data-testid="category-name-input"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="statut" className="border-0 border-b border-border/60 last:border-b-0">
                <AccordionTrigger className="py-4 text-sm font-medium hover:no-underline">
                  Statut
                </AccordionTrigger>
                <AccordionContent className="pb-4 pt-0">
                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/20 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Statut de la catégorie
                    </p>
                    <div className="space-y-3">
                      <p className="text-sm font-semibold text-foreground">Statut actuel</p>
                      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                        <button
                          type="button"
                          onClick={() => setCategoryFormData({ ...categoryFormData, is_active: true })}
                          className="inline-flex items-center gap-3 text-left text-sm font-medium text-foreground transition-opacity hover:opacity-90"
                          data-testid="category-status-active"
                        >
                          <span
                            className={cn(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[3px] border-2 border-orange-500 transition-colors',
                              categoryFormData.is_active !== false
                                ? 'bg-orange-500'
                                : 'bg-transparent',
                            )}
                            aria-hidden
                          >
                            {categoryFormData.is_active !== false && (
                              <Check className="h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </span>
                          Activé
                        </button>
                        <button
                          type="button"
                          onClick={() => setCategoryFormData({ ...categoryFormData, is_active: false })}
                          className="inline-flex items-center gap-3 text-left text-sm font-medium text-foreground transition-opacity hover:opacity-90"
                          data-testid="category-status-inactive"
                        >
                          <span
                            className={cn(
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[3px] border-2 border-orange-500 transition-colors',
                              categoryFormData.is_active === false
                                ? 'bg-orange-500'
                                : 'bg-transparent',
                            )}
                            aria-hidden
                          >
                            {categoryFormData.is_active === false && (
                              <Check className="h-3 w-3 text-white" strokeWidth={3} />
                            )}
                          </span>
                          Désactivé
                        </button>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>

          <SheetFooter className="mt-auto shrink-0 border-t border-border/60 px-6 py-4 sm:justify-end sm:space-x-2">
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmitCategory} data-testid="category-submit-btn">
              {selectedCategory ? 'Enregistrer' : 'Créer'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete Product Confirmation */}
      <AlertDialog open={deleteProductDialogOpen} onOpenChange={setDeleteProductDialogOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le produit ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le produit &quot;{selectedProduct?.name}&quot; sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProduct}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-product"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={deleteCategoryDialogOpen} onOpenChange={setDeleteCategoryDialogOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La catégorie &quot;{selectedCategory?.name}&quot; sera définitivement supprimée.
              <br /><br />
              <strong>Note :</strong> Une catégorie ne peut être supprimée que si aucun produit ne l&apos;utilise.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-category"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
